/**
 * World-space text presentation shared by Pixi labels.
 *
 * The overlay canvas intentionally stays at 1x resolution to protect GPU fill
 * rate. Small labels opt into a sharper text texture independently, so camera
 * zoom no longer enlarges a low-resolution glyph atlas.
 */
export const WORLD_TEXT_FONTS = {
  ui: '\'Inter\', \'Segoe UI\', system-ui, sans-serif',
  display: '\'Cinzel\', \'Georgia\', serif',
} as const;

export const WORLD_TEXT_QUALITY = {
  resolution: 2,
  roundPixels: true,
} as const;

/** Portal copy shares the idle portal's 3.2s, +/-15% breathing cadence. */
export const PORTAL_PROMPT_PRESENTATION = {
  fontFamily: WORLD_TEXT_FONTS.display,
  fontSize: 16,
  fontWeight: 'bold',
  letterSpacing: 0.7,
  color: 0xf4ead2,
  strokeWidth: 3,
  textResolution: WORLD_TEXT_QUALITY.resolution,
  roundPixels: WORLD_TEXT_QUALITY.roundPixels,
  alphaPulse: {
    periodMs: 3200,
    amplitude: 0.15,
  },
  /**
   * Distance-based reveal. The prompt used to be gated on literal hitbox
   * overlap, so a portal only named itself once you were standing inside it —
   * portals read as unlabelled scenery until you walked into one.
   *
   * Alpha now ramps linearly from `revealRadiusPx` (invisible) to
   * `fullOpacityRadiusPx` (fully legible), and overlap still pins it to 1.
   * Purely client-side presentation: the server's transfer authority in
   * PortalTransferPolicy.isPlayerAtPortal is untouched, so a readable label
   * never means a reachable portal.
   */
  reveal: {
    revealRadiusPx: 250,
    fullOpacityRadiusPx: 90,
  },
} as const;
